import React, { useEffect, useReducer } from 'react';

// Material UI
import {
    Grid, makeStyles, Checkbox, FormControlLabel, FormGroup,
    Button,
} from '@material-ui/core';

// Components and Others
import clsx from 'clsx';
import { isEmpty } from 'lodash';
import PropTypes from 'prop-types';
import KeyStore from 'utils/KeyStore';
import update from 'immutability-helper';
import ModalUtils from 'utils/ModalUtils';
import { Col, Form } from 'react-bootstrap';
import { FetchPolicy, MEDIA_QUERY } from 'utils/enum/Core';
import Select from 'components/widgets/Select';
import ButtonStyles from 'styles/theme/Button';
import Loading from 'components/widgets/Loading';
import Permissions from 'utils/enum/Permissions';
import { useMutation, useQuery } from '@apollo/client';
import CreditQuery from 'services/graphQL/query/credit/CreditQuery';
import CreditHelper from 'components/modules/crm/read/credit/CreditHelper';
import CreditMutation from 'services/graphQL/mutate/credit/CreditMutation';
import CreditHistory from 'components/modules/crm/read/credit/CreditHistory';
import CreditReportDialog from 'components/modules/crm/read/credit/CreditReportDialog';
import CreditScoreCardHistory from 'components/modules/crm/read/credit/CreditScoreCardHistory';
import StringUtils from 'lib/StringUtils';
import IntegrationType from 'utils/enum/ThirdPartyIntegrationsEnum';

const applicationType = {
    JOINT: 'Joint',
    INDIVIDUAL: 'Individual',
};
const modules = {
    CRM: 'CRM',
    DEAL: 'DEAL',
};
const applicationTypeOptions = Object.keys(applicationType).map((item) => ({ label: applicationType[item], value: applicationType[item] }));
const useStyles = makeStyles((theme) => ({
    creditOption: {
        background: theme.palette.background.white,
        marginRight: theme.spacing(2),
        padding: theme.spacing(2),
        minWidth: 250,
        width: 250,
        [theme.breakpoints.down(MEDIA_QUERY.W_992)]: {
            marginRight: '0',
            width: '100%',
            marginBottom: '22px',
        },
    },
    boxContainer: {
        background: theme.palette.background.default,
        height: '100%',
        overflow: 'auto',
        display: 'flex',
        width: '100%',
        padding: theme.spacing(2),
        [theme.breakpoints.down(MEDIA_QUERY.W_992)]: {
            flexDirection: 'column',
        },
    },
    container: {
        display: 'flex',
        flexWrap: 'initial',
        [theme.breakpoints.down(MEDIA_QUERY.W_992)]: {
            flexDirection: 'column',
            '& > .MuiGrid-item': {
                minWidth: '100%',
            },
        },
    },
    containerConfiguration: {
        width: '100%',
        height: '100%',
        background: theme.palette.background.white,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
    },
    buttonSpaces: {
        marginBottom: 10,
    },
    ...ButtonStyles.getStyle(theme),
}));
const ACTION_TYPE = {
    ON_CLOSE: 'onClose',
    ON_CHANGE_VALUE: 'onChangeValue',
    ON_SELECT_RECORD: 'onSelectRecord',
    SET_CREDIT_SCORE: 'setCreditScore',
    ON_CHANGE_BUREAUS: 'onChangeBureaus',
    SET_HISTORY_CREDITS: 'setHistoryCredits',
    SET_DEALER_INTEGRATIONS: 'setDealerIntegrations',
};
const reducer = (state, action) => {
    switch (action.type) {
    case ACTION_TYPE.SET_HISTORY_CREDITS:
        return update(state, {
            prospectHistory: { $set: action.payload.prospectHistory },
            coProspectHistory: { $set: action.payload.coProspectHistory },
            creditPullHistories: { $set: action.payload.creditPullHistories },
        });
    case ACTION_TYPE.ON_SELECT_RECORD:
        return update(state, {
            creditPullHistorySelected: { $set: action.payload },
        });
    case ACTION_TYPE.SET_DEALER_INTEGRATIONS:
        return update(state, {
            dealerIntegrations: { $set: action.payload },
        });
    case ACTION_TYPE.ON_CHANGE_BUREAUS:
        return update(state, {
            bureausByLot: { [action.payload.index]: { value: { $set: action.payload.value } } },
        });
    case ACTION_TYPE.ON_CHANGE_VALUE:
        const reportTypeChanging = action.payload.field === 'reportType';
        let bureaus = [];

        if (reportTypeChanging) {
            if (action.recordType === modules.CRM) {
                bureaus = state.dealerIntegrations?.find((item) => item.lotId === action.lotId && item.value === action.payload.value)?.bureaus;
            } else if (action.recordType === modules.DEAL) {
                bureaus = state.dealerIntegrations?.find((item) => item.lotName === action.lotName && item.value === action.payload.value)?.bureaus;
            }
        }

        return update(state, {
            [action.payload.field]: { $set: action.payload.value },
            bureausByLot: {
                $set: reportTypeChanging
                    ? (bureaus || []).filter((item) => item.active).map((item) => ({ label: item.name, value: item.defaultCheck }))
                    : state.bureausByLot,
            },
        });
    case ACTION_TYPE.SET_CREDIT_SCORE:
        return update(state, {
            creditScoreURL: { $set: action.payload?.url },
            open: { $set: true },
        });
    case ACTION_TYPE.ON_CLOSE:
        return update(state, {
            open: { $set: false },
        });
    default:
        return state;
    }
};
const initState = {
    open: false,
    bureausByLot: [],
    reportType: null,
    prospectHistory: [],
    creditScoreURL: null,
    applicationType: null,
    coProspectHistory: [],
    dealerIntegrations: [],
    creditPullHistories: [],
    creditPullHistorySelected: null,
};
const CreditDetail = ({
    recordId,
    recordType,
    lotId,
    lotName,
}) => {
    const keyStore = new KeyStore();
    const CRM_CREDIT_PULL_WRITE = keyStore.hasPermission(Permissions.CRM_CREDIT_PULL_WRITE);
    const SALES_CREDIT_PULL_WRITE = keyStore.hasPermission(Permissions.SALES_CREDIT_PULL_WRITE);
    const hasNoCreditPermissions = !CRM_CREDIT_PULL_WRITE && !SALES_CREDIT_PULL_WRITE;
    const classes = useStyles();
    const [state, dispatch] = useReducer(reducer, initState);
    const {
        data, error, loading, refetch,
    } = useQuery(CreditQuery.GET_HISTORY_CREDITS, {
        variables: { recordId, recordType },
        fetchPolicy: FetchPolicy.NETWORK_ONLY,
        skip: recordId === null || hasNoCreditPermissions,
    });
    const {
        data: dealerIntegrations, error: errorDealerIntegrations, loading: loadingDealerIntegrations,
    } = useQuery(CreditQuery.LIST_DEALER_INTEGRATIONS, {
        variables: { active: true },
        skip: hasNoCreditPermissions,
        fetchPolicy: FetchPolicy.NETWORK_ONLY,
    });
    const [generateCreditScore, { loading: loadingCreditScore }] = useMutation(CreditMutation.GENERATE_CREDIT_SCORE, {
        onCompleted: (response) => {
            if (!StringUtils.isEmpty(response?.generateCreditScore?.url)) {
                dispatch({ type: ACTION_TYPE.SET_CREDIT_SCORE, payload: response.generateCreditScore });
            }
        },
        onError: (errorMessage) => {
            const { message, graphQLErrors } = errorMessage;
            ModalUtils.errorMessage((graphQLErrors?.length > 0 ? graphQLErrors : null), message, {
                title: 'Message',
                description: 'The following information is required to pull the credit report:',
            });
        },
    });
    const [sendCreditScoreToThirdParty, { loading: sendingCreditScoreToThirdParty }] = useMutation(CreditMutation.SEND_CREDIT_SCORE_TO_THIRD_PARTY, {
        onCompleted: (response) => {
            if (response?.sendCreditScoreToThirdParty) {
                ModalUtils.successMessage(null, 'Information send successfully');
            }
        },
        onError: (errorMessage) => {
            const { message, graphQLErrors } = errorMessage;
            ModalUtils.errorMessage((graphQLErrors?.length > 0 ? graphQLErrors : null), message, {
                title: 'Message',
                description: 'The following problems needs to be fixed:',
            });
        },
    });

    const onSelectedRecord = (record) => {
        dispatch({
            type: ACTION_TYPE.ON_SELECT_RECORD,
            payload: record,
        });
    };

    const onChangeBureaus = (value, index) => {
        dispatch({
            type: ACTION_TYPE.ON_CHANGE_BUREAUS,
            payload: {
                value,
                index,
            },
        });
    };

    const onChangeValue = (field, value) => {
        dispatch({
            type: ACTION_TYPE.ON_CHANGE_VALUE,
            payload: {
                field,
                value,
            },
            lotId,
            lotName,
            recordType,
        });
    };

    const onClose = () => {
        dispatch({ type: ACTION_TYPE.ON_CLOSE });
        refetch();
    };

    const onRequestReport = () => {
        const multipleBureaus = CreditHelper.getCodeMultiBureaus(state.bureausByLot.filter((item) => item.value).map((item) => item.label));
        generateCreditScore({
            variables: {
                recordId,
                recordType,
                multipleBureaus,
                type: state.applicationType,
                uniqueCode: state.reportType,
            },
        });
    };

    const onSendCreditScoreToThirdParty = (losSystem) => {
        sendCreditScoreToThirdParty({
            variables: {
                recordId,
                losSystem,
                recordType,
                applicationType: state.applicationType,
            },
        });
    };

    useEffect(() => {
        // TODO: Show message error when repairing getHistoryCredits query.
        if (error) {
            return;
        }

        if (!loading && !isEmpty(data?.getHistoryCredits)) {
            const { getHistoryCredits } = data;
            dispatch({ type: ACTION_TYPE.SET_HISTORY_CREDITS, payload: getHistoryCredits });
        }
    }, [data, error, loading]);

    useEffect(() => {
        if (errorDealerIntegrations) {
            ModalUtils.errorMessage(errorDealerIntegrations?.graphQLErrors);
            return;
        }

        if (!loadingDealerIntegrations && !isEmpty(dealerIntegrations?.listDealerIntegrations)) {
            const { listDealerIntegrations } = dealerIntegrations;
            dispatch({
                type: ACTION_TYPE.SET_DEALER_INTEGRATIONS,
                payload: listDealerIntegrations,
                lotId,
                lotName,
                recordType,
            });
        }
    }, [dealerIntegrations, errorDealerIntegrations, loadingDealerIntegrations, lotId, lotName, recordType]);

    if (loadingDealerIntegrations) {
        return <Loading />;
    }

    const isCRM = recordType === modules.CRM;
    const reportTypeOptions = state.dealerIntegrations
        .filter((item) => {
            if (lotId === null) return item.lotName === lotName && item.type?.toUpperCase() === IntegrationType.CREDIT_PULL;
            return item.lotId === lotId && item.type?.toUpperCase() === IntegrationType.CREDIT_PULL;
        }).map((item) => ({ label: item.name, value: item.value }));
    if (isEmpty(reportTypeOptions)) {
        const moduleName = isCRM ? 'opportunity' : 'deal';
        const description = `Credit score is not available for the ${moduleName} lot.`;

        return (
            <div className={classes.boxContainer}>
                <div className={classes.containerConfiguration}>{description}</div>
            </div>
        );
    }

    return (
        <div className={classes.boxContainer}>
            {(CRM_CREDIT_PULL_WRITE || SALES_CREDIT_PULL_WRITE) && (
                <div className={clsx('box-shadow', classes.creditOption)}>
                    <Form.Group as={Col} className="p-0">
                        <Form.Label className={classes.labels}>Report Type</Form.Label>
                        <Select
                            size="sm"
                            name="reportType"
                            value={state.reportType}
                            onChange={onChangeValue}
                            options={reportTypeOptions}
                        />
                    </Form.Group>
                    <Form.Group as={Col} className="p-0">
                        <Form.Label className={classes.labels}>Application Type</Form.Label>
                        <Select
                            size="sm"
                            name="applicationType"
                            value={state.applicationType}
                            onChange={onChangeValue}
                            options={applicationTypeOptions}
                        />
                    </Form.Group>
                    <Form.Group as={Col} className="p-0">
                        <Form.Label className={classes.labels}>Bureaus Requested</Form.Label>
                        <FormGroup>
                            {state.bureausByLot.map((item, index) => (
                                <FormControlLabel
                                    key={index}
                                    control={<Checkbox name={item.label} checked={item.value} onChange={(e) => onChangeBureaus(e.target.checked, index)} />}
                                    label={item.label}
                                />
                            ))}
                        </FormGroup>
                    </Form.Group>
                    <Button
                        fullWidth
                        size="small"
                        variant="contained"
                        onClick={onRequestReport}
                        className={clsx(classes.containedSecondaryInfo, classes.buttonSpaces)}
                        disabled={state.applicationType === null || !state.bureausByLot.some((item) => item.value) || loadingCreditScore}
                    >
                        Request Report
                    </Button>
                    <Button
                        fullWidth
                        size="small"
                        variant="contained"
                        onClick={() => onSendCreditScoreToThirdParty('R1')}
                        className={clsx(classes.containedSecondaryInfo, classes.buttonSpaces)}
                        disabled={state.applicationType === null || sendingCreditScoreToThirdParty}
                    >
                        Send to Route One
                    </Button>
                    <Button
                        fullWidth
                        size="small"
                        variant="contained"
                        onClick={() => onSendCreditScoreToThirdParty('DT')}
                        className={clsx(classes.containedSecondaryInfo, classes.buttonSpaces)}
                        disabled={state.applicationType === null || sendingCreditScoreToThirdParty}
                    >
                        Send to Dealertrack
                    </Button>
                    <Button
                        fullWidth
                        size="small"
                        variant="contained"
                        onClick={() => onSendCreditScoreToThirdParty('CUDL')}
                        className={clsx(classes.containedSecondaryInfo, classes.buttonSpaces)}
                        disabled={state.applicationType === null || sendingCreditScoreToThirdParty}
                    >
                        Send to CUDL
                    </Button>
                </div>
            )}
            <Grid container spacing={2} className={classes.container} alignContent="flex-start">
                <Grid item xs={6}>
                    <CreditHistory
                        records={state.creditPullHistories}
                        onSelectedRecord={onSelectedRecord}
                        recordSelected={state.creditPullHistorySelected}
                    />
                </Grid>
                <Grid item xs={6} className={classes.column}>
                    <Grid container spacing={2} className={classes.personalInformation}>
                        <Grid item xs={12}>
                            <CreditScoreCardHistory
                                records={state.prospectHistory}
                                recordSelected={state.creditPullHistorySelected}
                                title={`Credit Score History - ${isCRM ? 'Prospect' : 'Buyer'}`}
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <CreditScoreCardHistory
                                records={state.coProspectHistory}
                                recordSelected={state.creditPullHistorySelected}
                                title={`Credit Score History - ${isCRM ? 'Co-Prospect' : 'Co-Buyer'}`}
                            />
                        </Grid>
                    </Grid>
                </Grid>
            </Grid>
            {state.open && <CreditReportDialog url={state.creditScoreURL} open={state.open} onClose={onClose} />}
        </div>
    );
};

CreditDetail.propTypes = {
    lotId: PropTypes.number,
    lotName: PropTypes.string,
    recordId: PropTypes.number,
    recordType: PropTypes.string.isRequired,
};

CreditDetail.defaultProps = {
    recordId: null,
    lotId: null,
    lotName: null,
};

export default CreditDetail;
